import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Circle, Clock, ArrowRight, BookOpen, Target, Lightbulb, AlertTriangle, ExternalLink } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { JourneyStep } from '@/hooks/useHomebuyingJourney';
import { journeyEducationalContent, StepEducationalContent } from '@/data/journeyEducationalContent';

interface JourneyStepDetailModalProps {
  step: JourneyStep | null;
  isOpen: boolean;
  onClose: () => void;
  onNavigateToStep: (stepId: string) => void;
}

export const JourneyStepDetailModal: React.FC<JourneyStepDetailModalProps> = ({
  step,
  isOpen,
  onClose,
  onNavigateToStep
}) => {
  if (!step) return null;

  const educationalContent: StepEducationalContent = journeyEducationalContent[step.id];
  if (!educationalContent) return null;

  const getStatusIcon = () => {
    if (step.isCompleted) {
      return <CheckCircle className="w-6 h-6 text-green-400" />;
    } else if (step.isCurrent) {
      return <Circle className="w-6 h-6 text-primary fill-current" />;
    } else {
      return <Circle className="w-6 h-6 text-muted-foreground" />;
    }
  };

  const getStatusText = () => {
    if (step.isCompleted) return "Completed";
    if (step.isCurrent) return "Current Step";
    return "Upcoming";
  };

  const getStatusColor = () => {
    if (step.isCompleted) return "text-green-400";
    if (step.isCurrent) return "text-primary";
    return "text-muted-foreground";
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="space-y-4">
          <div className="flex items-center gap-4">
            {getStatusIcon()}
            <div className="flex-1">
              <DialogTitle className="text-2xl font-bold">{step.title}</DialogTitle>
              <div className="flex items-center gap-4 mt-2">
                <Badge variant="secondary" className={getStatusColor()}>
                  {getStatusText()}
                </Badge>
                <div className="flex items-center gap-1">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">{step.estimatedTime}</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="space-y-2">
            <p className="text-muted-foreground">{step.description}</p>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>Step {step.order} of 5</span>
              <div className="flex-1">
                <Progress value={(step.order / 5) * 100} className="h-2" />
              </div>
            </div>
          </div>
        </DialogHeader>

        <Tabs defaultValue="overview" className="mt-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="details">What's Involved</TabsTrigger>
            <TabsTrigger value="insights">Why Important</TabsTrigger>
            <TabsTrigger value="tips">Tips & Resources</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6 mt-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-4"
            >
              <div className="p-4 rounded-lg bg-muted/50">
                <h3 className="font-semibold mb-2 flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-primary" />
                  Overview
                </h3>
                <p className="text-muted-foreground">{educationalContent.overview}</p>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 rounded-lg border">
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Target className="w-4 h-4 text-green-500" />
                    Key Outcomes
                  </h4>
                  <ul className="space-y-2">
                    {educationalContent.keyOutcomes.map((outcome, index) => (
                      <li key={index} className="flex items-start gap-2 text-sm">
                        <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        {outcome}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="p-4 rounded-lg border">
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Clock className="w-4 h-4 text-blue-500" />
                    Time Breakdown
                  </h4>
                  <ul className="space-y-2">
                    {educationalContent.estimatedTimeBreakdown.map((item, index) => (
                      <li key={index} className="flex justify-between text-sm">
                        <span>{item.task}</span>
                        <span className="text-muted-foreground">{item.time}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          </TabsContent>

          <TabsContent value="details" className="space-y-4 mt-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-4"
            >
              <div className="p-4 rounded-lg border">
                <h3 className="font-semibold mb-3">What's Involved</h3>
                <ul className="space-y-2">
                  {educationalContent.whatIsInvolved.map((item, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <Circle className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                      <span className="text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="p-4 rounded-lg border">
                <h3 className="font-semibold mb-3">What This Prepares You For</h3>
                <ul className="space-y-2">
                  {educationalContent.preparesYouFor.map((item, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <ArrowRight className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" />
                      <span className="text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </TabsContent>

          <TabsContent value="insights" className="space-y-4 mt-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-4 rounded-lg border"
            >
              <h3 className="font-semibold mb-3">Why This Step Is Important</h3>
              <ul className="space-y-3">
                {educationalContent.whyImportant.map((reason, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-semibold text-primary">{index + 1}</span>
                    </div>
                    <span className="text-sm">{reason}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </TabsContent>

          <TabsContent value="tips" className="space-y-4 mt-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-4"
            >
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 rounded-lg border">
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <Lightbulb className="w-4 h-4 text-yellow-500" />
                    Tips for Success
                  </h3>
                  <ul className="space-y-2">
                    {educationalContent.tips.map((tip, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <Lightbulb className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                        <span className="text-sm">{tip}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="p-4 rounded-lg border">
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-orange-500" />
                    Common Mistakes to Avoid
                  </h3>
                  <ul className="space-y-2">
                    {educationalContent.commonMistakes.map((mistake, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
                        <span className="text-sm">{mistake}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="p-4 rounded-lg border">
                <h3 className="font-semibold mb-3">Helpful Resources</h3>
                <div className="space-y-2">
                  {educationalContent.resources.map((resource, index) => (
                    <div key={index} className="flex items-center justify-between p-2 rounded hover:bg-muted/50 transition-colors">
                      <div>
                        <h4 className="font-medium text-sm">{resource.title}</h4>
                        <p className="text-xs text-muted-foreground">{resource.description}</p>
                      </div>
                      {resource.link && (
                        <ExternalLink className="w-4 h-4 text-muted-foreground" />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </TabsContent>
        </Tabs>

        <div className="flex justify-between items-center pt-6 border-t">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          
          <div className="flex gap-2">
            {!step.isCompleted && (
              <Button 
                onClick={() => {
                  onNavigateToStep(step.id);
                  onClose();
                }}
                className="bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-400 hover:to-purple-400"
              >
                {step.isCurrent ? 'Continue Step' : 'Start Step'}
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};